import { Injectable, inject } from '@angular/core';
import { LoggerService } from '../logger/logger.service';
import { environment } from '../../../environments/environment';
import {
  TelemetryRecord,
  TelemetryDeadLetterRecord,
  TELEMETRY_DB_NAME,
  TELEMETRY_DB_VERSION,
  TELEMETRY_EVENTS_STORE,
  TELEMETRY_DEAD_LETTER_STORE,
  TELEMETRY_META_STORE,
  BATCH_MAX_EVENTS,
  BATCH_MAX_BYTES,
  LEASE_DURATION_MS,
  SYNCED_RETENTION_MS,
  DEAD_LETTER_RETENTION_MS,
  MAX_QUEUE_EVENTS,
} from './telemetry.types';

const TAG = 'TelemetryEventStore';

/**
 * All IndexedDB access for the VDI telemetry queue.
 *
 * Responsibilities:
 *  - Open and migrate the database on first call.
 *  - Write incoming events, respecting queue-capacity limits.
 *  - Query pending records for a drain batch using valid numeric/string indexes.
 *  - Acquire and release IndexedDB leases for crash-recovery.
 *  - Mark records synced or increment their retry counters.
 *  - Move unrecoverable records to the dead-letter store.
 *  - Run periodic retention cleanup.
 *
 * Degrades gracefully if IndexedDB is blocked or unavailable.
 */
@Injectable({ providedIn: 'root' })
export class TelemetryEventStoreService {
  private logger = inject(LoggerService);
  private db: IDBDatabase | null = null;
  private dbPromise: Promise<IDBDatabase | null> | null = null;
  /** A unique string that identifies this tab instance for lease ownership. */
  private readonly instanceId = `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  // ─── DB lifecycle ────────────────────────────────────────────────────────

  private openDb(): Promise<IDBDatabase | null> {
    if (this.db) return Promise.resolve(this.db);
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = new Promise<IDBDatabase | null>((resolve) => {
      if (typeof indexedDB === 'undefined') {
        resolve(null);
        return;
      }

      let req: IDBOpenDBRequest;
      try {
        req = indexedDB.open(TELEMETRY_DB_NAME, TELEMETRY_DB_VERSION);
      } catch (err) {
        this.logger.warn(TAG, 'indexedDB.open threw', err);
        resolve(null);
        return;
      }

      req.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const tx = (event.target as IDBOpenDBRequest).transaction;
        this.migrate(db, tx, event.oldVersion);
      };

      req.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        this.dbPromise = null;
        resolve(this.db);
      };

      req.onerror = () => {
        this.logger.warn(TAG, 'IndexedDB open failed', req.error);
        this.dbPromise = null;
        resolve(null);
      };

      req.onblocked = () => {
        this.logger.warn(TAG, 'IndexedDB open blocked');
        resolve(null);
      };
    });

    return this.dbPromise;
  }

  private migrate(db: IDBDatabase, tx: IDBTransaction | null, oldVersion: number): void {
    let eventsStore: IDBObjectStore;
    if (oldVersion < 1 || !db.objectStoreNames.contains(TELEMETRY_EVENTS_STORE)) {
      eventsStore = db.createObjectStore(TELEMETRY_EVENTS_STORE, {
        keyPath: 'id',
        autoIncrement: true,
      });
      eventsStore.createIndex('by_next_attempt', 'nextAttemptAt');
      eventsStore.createIndex('by_session', 'vdiSessionId');
      eventsStore.createIndex('by_timestamp', 'timestamp');

      db.createObjectStore(TELEMETRY_DEAD_LETTER_STORE, {
        keyPath: 'id',
        autoIncrement: true,
      });

      db.createObjectStore(TELEMETRY_META_STORE, { keyPath: 'key' });
    } else if (tx) {
      eventsStore = tx.objectStore(TELEMETRY_EVENTS_STORE);
      if (!eventsStore.indexNames.contains('by_next_attempt')) {
        eventsStore.createIndex('by_next_attempt', 'nextAttemptAt');
      }
      if (!eventsStore.indexNames.contains('by_session')) {
        eventsStore.createIndex('by_session', 'vdiSessionId');
      }
      if (!eventsStore.indexNames.contains('by_timestamp')) {
        eventsStore.createIndex('by_timestamp', 'timestamp');
      }
    }
  }

  // ─── Write ───────────────────────────────────────────────────────────────

  /**
   * Persist a new event to the queue.
   * Returns `'queued'`, `'dropped-capacity'`, `'dropped-payload-too-large'`, or `'failed'`.
   */
  async enqueue(
    record: Omit<TelemetryRecord, 'id' | 'isSynced' | 'createdAt' | 'attemptCount' | 'nextAttemptAt' | 'leaseOwner' | 'leaseUntil' | 'lastError'>,
  ): Promise<'queued' | 'dropped-capacity' | 'dropped-payload-too-large' | 'failed'> {
    // Reject oversized payloads before opening a transaction.
    if (new TextEncoder().encode(record.payload).length > 64 * 1024) {
      return 'dropped-payload-too-large';
    }

    const db = await this.openDb();
    if (!db) return 'failed';

    return new Promise((resolve) => {
      try {
        const tx = db.transaction([TELEMETRY_EVENTS_STORE], 'readwrite');
        const store = tx.objectStore(TELEMETRY_EVENTS_STORE);

        // Count only pending (unsynced) records against max capacity (excluding 7-day retained synced rows)
        let countReq: IDBRequest<number>;
        if (store.indexNames.contains('by_next_attempt')) {
          countReq = store.index('by_next_attempt').count(IDBKeyRange.upperBound(1e14));
        } else {
          countReq = store.count();
        }
        const maxEvents = environment.telemetry?.maxQueueEvents ?? MAX_QUEUE_EVENTS;
        countReq.onsuccess = () => {
          if (countReq.result >= maxEvents) {
            resolve('dropped-capacity');
            tx.abort();
            return;
          }

          const full: TelemetryRecord = {
            ...record,
            isSynced: false,
            createdAt: Date.now(),
            attemptCount: 0,
            nextAttemptAt: 0,
            leaseOwner: null,
            leaseUntil: null,
            lastError: null,
          };

          const addReq = store.add(full);
          addReq.onsuccess = () => resolve('queued');
          addReq.onerror = () => {
            this.logger.warn(TAG, 'enqueue add failed', addReq.error);
            resolve('failed');
          };
        };
        countReq.onerror = () => {
          this.logger.warn(TAG, 'enqueue count failed', countReq.error);
          resolve('failed');
        };
      } catch (err) {
        this.logger.warn(TAG, 'enqueue transaction failed', err);
        resolve('failed');
      }
    });
  }

  // ─── Pending batch query ──────────────────────────────────────────────────

  /**
   * Read up to {@link BATCH_MAX_EVENTS} pending records capped at
   * {@link BATCH_MAX_BYTES}. A forced drain includes retry-delayed records for
   * explicit recovery at application start and VDI session end.
   */
  async getPendingBatch(force = false): Promise<TelemetryRecord[]> {
    const db = await this.openDb();
    if (!db) return [];

    return new Promise((resolve) => {
      try {
        const tx = db.transaction([TELEMETRY_EVENTS_STORE], 'readonly');
        const store = tx.objectStore(TELEMETRY_EVENTS_STORE);
        const now = Date.now();

        let req: IDBRequest<IDBCursorWithValue | null>;
        if (!force && store.indexNames.contains('by_next_attempt')) {
          const index = store.index('by_next_attempt');
          req = index.openCursor(IDBKeyRange.upperBound(now));
        } else {
          req = store.openCursor();
        }

        const results: TelemetryRecord[] = [];
        let totalBytes = 0;
        const maxBatch = environment.telemetry?.batchMaxEvents ?? BATCH_MAX_EVENTS;

        req.onsuccess = () => {
          const cursor = req.result;
          if (!cursor || results.length >= maxBatch) {
            resolve(results);
            return;
          }
          const record = cursor.value as TelemetryRecord;
          // Normal drains respect retry backoff; explicit recovery drains do not.
          if (record.isSynced || (!force && record.nextAttemptAt > now)) {
            cursor.continue();
            return;
          }
          // Skip records currently leased by another instance.
          if (record.leaseOwner && record.leaseUntil && record.leaseUntil > now && record.leaseOwner !== this.instanceId) {
            cursor.continue();
            return;
          }
          const bytes = new TextEncoder().encode(record.payload).length;
          if (totalBytes + bytes > BATCH_MAX_BYTES) {
            resolve(results);
            return;
          }
          totalBytes += bytes;
          results.push(record);
          cursor.continue();
        };

        req.onerror = () => {
          this.logger.warn(TAG, 'getPendingBatch cursor failed', req.error);
          resolve([]);
        };
      } catch (err) {
        this.logger.warn(TAG, 'getPendingBatch transaction failed', err);
        resolve([]);
      }
    });
  }

  // ─── Lease management ────────────────────────────────────────────────────

  /** Atomically acquire leases on the given record IDs. */
  async acquireLeases(ids: number[]): Promise<void> {
    if (!ids.length) return;
    const db = await this.openDb();
    if (!db) return;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction([TELEMETRY_EVENTS_STORE], 'readwrite');
        const store = tx.objectStore(TELEMETRY_EVENTS_STORE);
        const until = Date.now() + LEASE_DURATION_MS;

        for (const id of ids) {
          const req = store.get(id);
          req.onsuccess = () => {
            const record = req.result as TelemetryRecord | undefined;
            if (record) {
              record.leaseOwner = this.instanceId;
              record.leaseUntil = until;
              store.put(record);
            }
          };
        }

        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch (err) {
        this.logger.warn(TAG, 'acquireLeases failed', err);
        resolve();
      }
    });
  }

  /** Release leases for the given record IDs (without changing any other field). */
  async releaseLeases(ids: number[]): Promise<void> {
    if (!ids.length) return;
    const db = await this.openDb();
    if (!db) return;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction([TELEMETRY_EVENTS_STORE], 'readwrite');
        const store = tx.objectStore(TELEMETRY_EVENTS_STORE);

        for (const id of ids) {
          const req = store.get(id);
          req.onsuccess = () => {
            const record = req.result as TelemetryRecord | undefined;
            if (record && record.leaseOwner === this.instanceId) {
              record.leaseOwner = null;
              record.leaseUntil = null;
              store.put(record);
            }
          };
        }

        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch (err) {
        this.logger.warn(TAG, 'releaseLeases failed', err);
        resolve();
      }
    });
  }

  // ─── Sync outcome updates ─────────────────────────────────────────────────

  /** Mark the given IDs as synced and clear their leases. */
  async markSynced(ids: number[]): Promise<void> {
    if (!ids.length) return;
    const db = await this.openDb();
    if (!db) return;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction([TELEMETRY_EVENTS_STORE], 'readwrite');
        const store = tx.objectStore(TELEMETRY_EVENTS_STORE);

        for (const id of ids) {
          const req = store.get(id);
          req.onsuccess = () => {
            const record = req.result as TelemetryRecord | undefined;
            if (record) {
              record.isSynced = true;
              record.nextAttemptAt = Infinity; // Excludes synced rows from index scans
              record.leaseOwner = null;
              record.leaseUntil = null;
              store.put(record);
            }
          };
        }

        tx.oncomplete = () => {
          this.logger.info(TAG, `successfully committed isSynced=true for IDs: ${ids.join(', ')}`);
          resolve();
        };
        tx.onerror = (err) => {
          this.logger.warn(TAG, 'markSynced transaction failed', err);
          resolve();
        };
      } catch (err) {
        this.logger.warn(TAG, 'markSynced failed', err);
        resolve();
      }
    });
  }

  /**
   * Increment `attemptCount`, set `nextAttemptAt` using the provided delay,
   * record the last error, and clear the lease for each given ID.
   */
  async markRetry(ids: number[], nextDelayMs: number, errorMsg: string): Promise<void> {
    if (!ids.length) return;
    const db = await this.openDb();
    if (!db) return;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction([TELEMETRY_EVENTS_STORE], 'readwrite');
        const store = tx.objectStore(TELEMETRY_EVENTS_STORE);
        const nextAt = Date.now() + nextDelayMs;

        for (const id of ids) {
          const req = store.get(id);
          req.onsuccess = () => {
            const record = req.result as TelemetryRecord | undefined;
            if (record) {
              record.attemptCount += 1;
              record.nextAttemptAt = nextAt;
              record.lastError = errorMsg;
              record.leaseOwner = null;
              record.leaseUntil = null;
              store.put(record);
            }
          };
        }

        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch (err) {
        this.logger.warn(TAG, 'markRetry failed', err);
        resolve();
      }
    });
  }

  // ─── Dead-letter ──────────────────────────────────────────────────────────

  /** Move a record to the dead-letter store and delete it from the main queue. */
  async moveToDeadLetter(record: TelemetryRecord, reason: string): Promise<void> {
    const db = await this.openDb();
    if (!db) return;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction([TELEMETRY_EVENTS_STORE, TELEMETRY_DEAD_LETTER_STORE], 'readwrite');
        const eventsStore = tx.objectStore(TELEMETRY_EVENTS_STORE);
        const dlStore = tx.objectStore(TELEMETRY_DEAD_LETTER_STORE);

        const dl: TelemetryDeadLetterRecord = {
          originalId: record.id ?? null,
          vdiSessionId: record.vdiSessionId,
          eventType: record.eventType,
          payload: record.payload,
          timestamp: record.timestamp,
          sequence: record.sequence,
          reason,
          createdAt: Date.now(),
        };

        dlStore.add(dl);
        if (record.id != null) eventsStore.delete(record.id);

        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch (err) {
        this.logger.warn(TAG, 'moveToDeadLetter failed', err);
        resolve();
      }
    });
  }

  // ─── Retention cleanup ────────────────────────────────────────────────────

  /** Delete synced records older than {@link SYNCED_RETENTION_MS} and dead-letter records older than {@link DEAD_LETTER_RETENTION_MS}. */
  async runRetentionCleanup(): Promise<void> {
    const db = await this.openDb();
    if (!db) return;
    const now = Date.now();
    await this.cleanSyncedEvents(db, now);
    await this.cleanDeadLetter(db, now);
  }

  private cleanSyncedEvents(db: IDBDatabase, now: number): Promise<void> {
    return new Promise((resolve) => {
      try {
        const tx = db.transaction([TELEMETRY_EVENTS_STORE], 'readwrite');
        const store = tx.objectStore(TELEMETRY_EVENTS_STORE);
        const cutoff = now - SYNCED_RETENTION_MS;
        const req = store.openCursor();
        req.onsuccess = () => {
          const cursor = req.result;
          if (cursor) {
            const rec = cursor.value as TelemetryRecord;
            if (rec.isSynced && rec.createdAt < cutoff) {
              cursor.delete();
            }
            cursor.continue();
          } else {
            resolve();
          }
        };
        req.onerror = () => resolve();
      } catch {
        resolve();
      }
    });
  }

  private cleanDeadLetter(db: IDBDatabase, now: number): Promise<void> {
    return new Promise((resolve) => {
      try {
        const cutoff = now - DEAD_LETTER_RETENTION_MS;
        const tx = db.transaction([TELEMETRY_DEAD_LETTER_STORE], 'readwrite');
        const store = tx.objectStore(TELEMETRY_DEAD_LETTER_STORE);
        const req = store.openCursor();
        req.onsuccess = () => {
          const cursor = req.result;
          if (cursor) {
            const rec = cursor.value as TelemetryDeadLetterRecord;
            if (rec.createdAt < cutoff) cursor.delete();
            cursor.continue();
          } else {
            resolve();
          }
        };
        req.onerror = () => resolve();
      } catch {
        resolve();
      }
    });
  }

  // ─── Diagnostics & Dev Monitoring ───────────────────────────────────────

  /** Count of pending (not-yet-synced) records for observability logging. */
  async getPendingCount(): Promise<number> {
    const db = await this.openDb();
    if (!db) return 0;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction([TELEMETRY_EVENTS_STORE], 'readonly');
        const store = tx.objectStore(TELEMETRY_EVENTS_STORE);
        const req = store.openCursor();
        let count = 0;
        req.onsuccess = () => {
          const cursor = req.result;
          if (cursor) {
            const rec = cursor.value as TelemetryRecord;
            if (!rec.isSynced) count++;
            cursor.continue();
          } else {
            resolve(count);
          }
        };
        req.onerror = () => resolve(0);
      } catch {
        resolve(0);
      }
    });
  }

  /** Get full diagnostics summary for Developer / QA Monitoring UI. */
  async getStoreStats(): Promise<{ total: number; pending: number; synced: number; deadLetter: number }> {
    const db = await this.openDb();
    if (!db) return { total: 0, pending: 0, synced: 0, deadLetter: 0 };

    return new Promise((resolve) => {
      try {
        const tx = db.transaction([TELEMETRY_EVENTS_STORE, TELEMETRY_DEAD_LETTER_STORE], 'readonly');
        const eventsStore = tx.objectStore(TELEMETRY_EVENTS_STORE);
        const dlStore = tx.objectStore(TELEMETRY_DEAD_LETTER_STORE);

        let total = 0;
        let pending = 0;
        let synced = 0;
        let deadLetter = 0;

        const dlReq = dlStore.count();
        dlReq.onsuccess = () => { deadLetter = dlReq.result; };

        const req = eventsStore.openCursor();
        req.onsuccess = () => {
          const cursor = req.result;
          if (cursor) {
            total++;
            const rec = cursor.value as TelemetryRecord;
            if (rec.isSynced) synced++;
            else pending++;
            cursor.continue();
          } else {
            resolve({ total, pending, synced, deadLetter });
          }
        };
        req.onerror = () => resolve({ total, pending, synced, deadLetter });
      } catch {
        resolve({ total: 0, pending: 0, synced: 0, deadLetter: 0 });
      }
    });
  }

  /** Retrieve all events sorted from newest to oldest for Monitoring UI. */
  async getAllEvents(limit = 100): Promise<TelemetryRecord[]> {
    const db = await this.openDb();
    if (!db) return [];

    return new Promise((resolve) => {
      try {
        const tx = db.transaction([TELEMETRY_EVENTS_STORE], 'readonly');
        const store = tx.objectStore(TELEMETRY_EVENTS_STORE);
        const req = store.openCursor(null, 'prev');
        const list: TelemetryRecord[] = [];

        req.onsuccess = () => {
          const cursor = req.result;
          if (cursor && list.length < limit) {
            list.push(cursor.value as TelemetryRecord);
            cursor.continue();
          } else {
            resolve(list);
          }
        };
        req.onerror = () => resolve([]);
      } catch {
        resolve([]);
      }
    });
  }

  /** Clear all local telemetry records (useful for QA / testing reset). */
  async clearAllTelemetry(): Promise<void> {
    const db = await this.openDb();
    if (!db) return;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction([TELEMETRY_EVENTS_STORE, TELEMETRY_DEAD_LETTER_STORE], 'readwrite');
        tx.objectStore(TELEMETRY_EVENTS_STORE).clear();
        tx.objectStore(TELEMETRY_DEAD_LETTER_STORE).clear();
        tx.oncomplete = () => {
          this.logger.info(TAG, 'all telemetry stores cleared');
          resolve();
        };
        tx.onerror = () => resolve();
      } catch {
        resolve();
      }
    });
  }

  /** Delete only records that have already been confirmed synced (isSynced: true). */
  async deleteSyncedOnly(): Promise<number> {
    const db = await this.openDb();
    if (!db) return 0;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction([TELEMETRY_EVENTS_STORE], 'readwrite');
        const store = tx.objectStore(TELEMETRY_EVENTS_STORE);
        const req = store.openCursor();
        let deletedCount = 0;

        req.onsuccess = () => {
          const cursor = req.result;
          if (cursor) {
            const rec = cursor.value as TelemetryRecord;
            if (rec.isSynced) {
              cursor.delete();
              deletedCount++;
            }
            cursor.continue();
          } else {
            resolve(deletedCount);
          }
        };
        req.onerror = () => resolve(0);
      } catch {
        resolve(0);
      }
    });
  }
}

